import React, { Suspense, useMemo } from 'react';
import { Grid } from '@mui/material';
import {
  GlobalStyles,
  IBuilderData,
  IComponentBuilderConfig,
  IWidget,
  PAGE_KEY,
} from '../../../../shared/src/types/builder/builder.types';
import { COMPONENT_KEY, VARIANT_KEY, SCREEN_SIZE, COMPONENT_MAP, HEADER_STATE } from '../../sections/Commons';
import { BoxComponent } from '../../base/box/Box';
import { InlineTextStyles } from '../../sections/Commons/common.types';

enum CONFIG_PANEL_KEYS {
  BASIC_CONFIG = 'BASIC_CONFIG',
  GLOBAL_STYLES = 'GLOBAL_STYLES',
  NEW_PAGE = 'NEW_PAGE',
  NEW_SECTION = 'NEW_SECTION',
  EDIT_SECTION = 'EDIT_SECTION',
  IMAGE_CUSTOMIZATION = 'IMAGE_CUSTOMIZATION',
  INLINE_TEXT_STYLES = 'INLINE_TEXT_STYLES',
}

interface RenderWebsiteProps {
  componentData: IWidget;
  builderConfig?: IComponentBuilderConfig;
  globalStyles?: GlobalStyles;
  isPreview?: boolean;
  data?: any;
  isPublishedDesktopMode?: boolean;
  MediaMap?: any;
  slug?: string;
  setData?: (updater: (draft: IBuilderData) => void) => void;
  isBuilderMode?: boolean;
  editorId?: boolean;
  pageName?: string;
  componentOrderNumber: number;
  tabIndex?: number;
  LinkComponent?: any;
  ImageComponent?: any;
  seoTitle?: string;
  prefetchedComponentData?: Record<string, any>;
  onTextSelect?: (
    data: {
      text: string;
      range: { start: number; end: number };
      currentStyles?: InlineTextStyles;
      dataKey: string;
    } | null
  ) => void;
}

const hasHeadingEnabled = (widget?: IWidget | null) =>
  Boolean(widget && !widget.disabled && widget.props?.config?.heading?.toggle === true);

const resolvePrimaryHeadingComponentId = (builderData?: IBuilderData): string | null => {
  if (!builderData?.activePage) return null;
  const page = builderData.pages?.[builderData.activePage];
  if (!page) return null;

  if (hasHeadingEnabled(page.MainComponent)) {
    return page.MainComponent.id;
  }

  for (const componentId of page.componentOrder || []) {
    const widget = page.components?.[componentId];
    if (hasHeadingEnabled(widget)) {
      return widget.id;
    }
  }

  return null;
};

export function RenderWebsite({
  componentData,
  builderConfig: previewBuilderConfig,
  globalStyles: previewGlobalStyles,
  data,
  isPublishedDesktopMode = false,
  isPreview = false,
  MediaMap,
  slug,
  setData,
  isBuilderMode = false,
  editorId = false,
  pageName = data ? data.pages[data.activePage]?.meta?.label : '',
  componentOrderNumber,
  tabIndex,
  LinkComponent,
  ImageComponent,
  seoTitle,
  prefetchedComponentData,
  onTextSelect,
}: RenderWebsiteProps) {
  const COMPONENT_ID = componentData.id;
  const isComponentInitialized = componentData.isInitialized;
  const isActive =
    isBuilderMode && !data.builderBasicConfig.previewMode ? data.activeComponent.id === COMPONENT_ID : false;

  const navLinks = data?.navLinks || previewBuilderConfig?.navLinks;
  const pageUrls = data?.pageUrls || previewBuilderConfig?.pageUrls;
  const isAccessibility = Object.keys(data?.pages || {}).includes(PAGE_KEY.ACCESSIBILITY);

  // Get pre-fetched data for this specific component if available
  const componentPrefetchedData = prefetchedComponentData?.[COMPONENT_ID];

  let builderConfig: IComponentBuilderConfig | null =
    previewBuilderConfig ||
    (data
      ? {
          builderMode: isBuilderMode && data.builderBasicConfig.builderMode,
          previewMode: isBuilderMode && data.builderBasicConfig.previewMode,
          screenSize: data.builderBasicConfig?.screenSize,
          logoConfig: data.logoConfig,
          favicon: data.favicon,
          navLinks,
          pageUrls,
          brandbook: data.brandbook,
          isAccessibility,
          isPublishedDesktopMode,
          isBuilderDesktopMode: data.builderBasicConfig?.screenSize === SCREEN_SIZE.DESKTOP,
          isComponentActive: isBuilderMode ? data.activeComponent.id === COMPONENT_ID : false,
          socials: data.socials,
          onBoarding: data.onBoarding,
          builderConfigData: {
            ...data.builderConfigData,
            pageName,
            prefetchedComponentData: componentPrefetchedData,
          },
          slug: slug,
          componentOrderNumber,
          colors: {
            primary: data.brandbook.colors?.[0]?.hex || null,
            secondary: data.brandbook.colors?.[1]?.hex || null,
            tertiary: data.brandbook.colors?.[2]?.hex || null,
          },
          activePage: data.activePage,
          stickyButtonToggle: data.stickyButtonToggle,
          seoTitle,
        }
      : null);

  const isDesktop = !!(
    builderConfig?.isBuilderDesktopMode ||
    builderConfig?.isPublishedDesktopMode ||
    data?.builderBasicConfig?.screenSize === SCREEN_SIZE.DESKTOP
  );

  let component: IWidget = componentData as IWidget;
  if (data || !isPreview) {
    if (componentData.component === COMPONENT_KEY.HEADER) {
      component = data.HeaderComponent;
    } else if (componentData.component === COMPONENT_KEY.FOOTER) {
      component = data.FooterComponent;
    } else if (
      componentData.component === COMPONENT_KEY.HERO ||
      componentData.component === COMPONENT_KEY.ACCESSIBILITY
    ) {
      component = data.pages[data.activePage].MainComponent;
    } else {
      component = data.pages[data.activePage].components[COMPONENT_ID];
    }
  }

  const setComponentData = (key: string, value: string) => {
    if (!setData) return;

    setData((draft: IBuilderData) => {
      let current: any = {};
      if (component.component === COMPONENT_KEY.HEADER) {
        current = draft.HeaderComponent.props.config;
      } else if (component.component === COMPONENT_KEY.FOOTER) {
        current = draft.FooterComponent.props.config;
      } else if (component.component === COMPONENT_KEY.HERO || component.component === COMPONENT_KEY.ACCESSIBILITY) {
        current = draft.pages[draft.activePage].MainComponent.props.config;
      } else {
        current = draft.pages[draft.activePage].components[COMPONENT_ID].props.config;
      }

      if (key) {
        const keys = key?.split('.');

        keys?.slice(0, -1).forEach((k) => {
          if (!current[k]) {
            current[k] = {};
          }
          current = current[k];
        });

        current[keys[keys.length - 1]] = value;
      }
    });
  };

  const primaryHeadingComponentId = data ? resolvePrimaryHeadingComponentId(data) : null;
  const shouldUsePrimaryHeading = data
    ? Boolean(component?.id && component?.id === primaryHeadingComponentId)
    : Boolean(builderConfig?.shouldUsePrimaryHeading);

  if (builderConfig) {
    builderConfig = {
      ...builderConfig,
      shouldUsePrimaryHeading,
    };
  }

  const Component = useMemo(() => {
    const ComponentType: any = COMPONENT_MAP?.[componentData.component as COMPONENT_KEY];
    if (ComponentType?.[componentData.variant] && component) {
      const Component = ComponentType[componentData.variant];
      const componentProps = {
        globalStyles: previewGlobalStyles || { ...data.globalStyles, headerVariant: data.HeaderComponent.variant },
        localStyles: {},
        builderConfig,
        componentConfig: {},
        MediaMap,
        setComponentData: isBuilderMode ? setComponentData : undefined,
        componentId: COMPONENT_ID,
        LinkComponent,
        ImageComponent,
        onTextSelect: isBuilderMode
          ? (data: any) => {
              if (data && setData) {
                setData((draft: IBuilderData) => {
                  draft.activeComponent = {
                    id: COMPONENT_ID,
                    type: component.component as COMPONENT_KEY,
                    variant: component.variant as VARIANT_KEY,
                  };
                });
              }
              if (onTextSelect) {
                onTextSelect(data);
              }
            }
          : undefined,
      };

      if (isComponentInitialized || isPreview) {
        const mobileConfig = component.props.config?.['media-s'] || {};
        if (!isDesktop && mobileConfig && Object.keys(mobileConfig).length > 0) {
          const desktopConfig = component.props.config;
          const mergedConfig = { ...desktopConfig };

          if (mobileConfig.bodyText) {
            mergedConfig.bodyText = {
              ...desktopConfig.bodyText,
              ...mobileConfig.bodyText,
              text: desktopConfig.bodyText?.text,
            };
          }

          if (mobileConfig.heading) {
            mergedConfig.heading = {
              ...desktopConfig.heading,
              ...mobileConfig.heading,
              text: desktopConfig.heading?.text,
            };
          }
          if (mobileConfig.subheading) {
            mergedConfig.subheading = {
              ...desktopConfig.subheading,
              ...mobileConfig.subheading,
              text: desktopConfig.subheading?.text,
            };
          }
          if (mobileConfig.overline) {
            mergedConfig.overline = {
              ...desktopConfig.overline,
              ...mobileConfig.overline,
              text: desktopConfig.overline?.text,
            };
          }

          if (mobileConfig.button) {
            mergedConfig.button = {
              ...desktopConfig.button,
              ...mobileConfig.button,
              text: desktopConfig.button?.text,
              link: desktopConfig.button?.link,
            };
          }

          if (mobileConfig.secondaryButton) {
            mergedConfig.secondaryButton = {
              ...desktopConfig.secondaryButton,
              ...mobileConfig.secondaryButton,
              text: desktopConfig.secondaryButton?.text,
              link: desktopConfig.secondaryButton?.link,
            };
          }

          Object.keys(mobileConfig).forEach((key) => {
            if (!['bodyText', 'heading', 'subheading', 'overline', 'button', 'secondaryButton'].includes(key)) {
              mergedConfig[key] = mobileConfig[key];
            }
          });

          componentProps.componentConfig = mergedConfig;
        } else {
          componentProps.componentConfig = component.props.config;
        }

        componentProps.localStyles = component.props.styles;
      } else {
        componentProps.localStyles = Component.initialProps?.localStyles;
        componentProps.componentConfig = Component.initialProps?.componentConfig;
      }

      const propsObject = {
        config: componentProps.componentConfig,
        styles: componentProps.localStyles,
      };

      if (isBuilderMode && !isComponentInitialized) {
        setData?.((draft: IBuilderData) => {
          if (component.component === COMPONENT_KEY.HEADER) {
            draft.HeaderComponent.props = propsObject;
            draft.HeaderComponent.isInitialized = true;
          } else if (component.component === COMPONENT_KEY.FOOTER) {
            draft.FooterComponent.props = propsObject;
            draft.FooterComponent.isInitialized = true;
          } else if (
            component.component === COMPONENT_KEY.HERO ||
            component.component === COMPONENT_KEY.ACCESSIBILITY
          ) {
            draft.pages[draft.activePage].MainComponent.props = propsObject;
            draft.pages[draft.activePage].MainComponent.isInitialized = true;
          } else {
            draft.pages[data.activePage].components[COMPONENT_ID].props = propsObject;
            draft.pages[data.activePage].components[COMPONENT_ID].isInitialized = true;
          }
        });
      }

      return React.cloneElement(Component.component, componentProps);
    }
  }, [
    JSON.stringify(componentData),
    JSON.stringify(builderConfig),
    JSON.stringify(previewGlobalStyles || data.globalStyles),
    data?.builderBasicConfig?.screenSize,
    onTextSelect,
  ]);

  const borderWidth = component.component === COMPONENT_KEY.HEADER ? 2 : 4;

  const designPreview = !isBuilderMode && data?.builderBasicConfig?.previewMode;

  const toolbarPosition: React.CSSProperties['position'] = 'absolute';

  const getHeaderPosition = (): React.CSSProperties['position'] => {
    if (component.component !== COMPONENT_KEY.HEADER) {
      return 'relative';
    }

    const isInBuilderMode = !!builderConfig?.builderMode;

    if (!isInBuilderMode) {
      return data?.globalStyles?.headerState !== HEADER_STATE.DEFAULT ? 'absolute' : 'unset';
    }

    if (designPreview === undefined) {
      return 'absolute';
    }

    if (
      isDesktop ||
      component.variant === VARIANT_KEY.VARIANT3 ||
      (component.variant === VARIANT_KEY.VARIANT2 && data?.globalStyles?.headerState === HEADER_STATE.DEFAULT)
    ) {
      return 'unset';
    }

    if (data?.globalStyles?.headerState === HEADER_STATE.DEFAULT) {
      return 'unset';
    }

    return 'absolute';
  };

  const getTopSpacing = (): React.CSSProperties['marginTop'] => {
    if (
      !data?.builderBasicConfig?.builderMode &&
      isDesktop &&
      componentOrderNumber === 1 &&
      component.component !== COMPONENT_KEY.HEADER &&
      data?.HeaderComponent?.variant !== VARIANT_KEY.VARIANT3
    ) {
      return data?.globalStyles?.headerState === HEADER_STATE.STICKY ? '110px' : '0';
    }

    return '0';
  };

  return (
    <BoxComponent
      className="selectable-paper"
      id={componentData.id}
      style={{
        border: isActive ? `${borderWidth}px solid #008279` : '0',
        position: getHeaderPosition(),
        width: designPreview === undefined ? '100%' : undefined,
        padding: isActive || !isBuilderMode ? '0px' : '0px 4px',
        borderRadius: isBuilderMode ? '6px 6px 0 0' : '0',
        wordBreak: 'break-word',
        paddingTop: getTopSpacing(),
      }}
      onClick={() =>
        isBuilderMode &&
        setData?.((draft: IBuilderData) => {
          draft.activeConfigPanel = CONFIG_PANEL_KEYS.BASIC_CONFIG;
          draft.activeComponent = {
            id: COMPONENT_ID,
            type: component.component,
            variant: component.variant as VARIANT_KEY,
          };
        })
      }
      tabIndex={tabIndex}
    >
      {isActive && (
        <Grid
          container
          justifyContent={'end'}
          style={{
            background: 'transparent',
            position: toolbarPosition,
            width: '-webkit-fill-available',
            top: '0',
            zIndex: 1000,
          }}
        >
          <BoxComponent
            sx={{
              background: '#008279',
              fontSize: '25px',
              color: '#fff',
              padding: '5px 14px',
              minWidth: '80px',
              textAlign: 'center',
            }}
          >
            {componentData.name}
          </BoxComponent>
        </Grid>
      )}
      {editorId ? (
        <BoxComponent id="editor">
          {component.component === COMPONENT_KEY.HEADER || component.component === COMPONENT_KEY.HERO ? (
            Component
          ) : (
            <Suspense>{Component}</Suspense>
          )}
        </BoxComponent>
      ) : component.component === COMPONENT_KEY.HEADER || component.component === COMPONENT_KEY.HERO ? (
        Component
      ) : (
        <Suspense>{Component}</Suspense>
      )}
    </BoxComponent>
  );
}
